import React from 'react';
import { useStore } from '@store';

export enum FeaturePermission {
  CUSTOMER = 'customer',
  DISPATCHING = 'dispatching',
  DISPATCHING_CHAT = 'dispatching-chat',
  DISPATCHING_GEOGRAPHY = 'dispatching-geography',
  DISPATCHING_REGIONS = 'dispatching-regions',
  DRIVER = 'driver',
  DRIVER_CERTIFICATIONS = 'driver-certifications',
  DRIVER_EQUIPMENT_MANAGEMENT = 'driver-equipment-management',
  DRIVER_ONBOARDING = 'driver-onboarding',
  DRIVER_PAY = 'driver-pay',
  EVENT_LOGS = 'eventlogs',
  FINANCE = 'finance',
  HANGTAGS = 'hangtags',
  LEARNING_MODULE = 'learning-module',
  OUTCOMES = 'outcomes',
  RISK_ASSESSMENT = 'risk-assessment',
  SETTINGS = 'settings',
  TECH = 'tech',
  USER_MANAGEMENT = 'usermanagement',
  WORKFLOWS = 'workflows',
  PROMOS = 'promos',
}

/**
 * Check if user has a specific feature permission
 */
export const hasFeaturePermission = (userPermissions: string[], requiredPermission: FeaturePermission): boolean => {
  return userPermissions.includes(requiredPermission);
};

/**
 * React hook to check if current user has a specific feature permission
 */
export const useFeaturePermission = (requiredPermission: FeaturePermission): boolean => {
  const permissions = useStore(state => state.permissions || []);
  const hasPermission = hasFeaturePermission(permissions, requiredPermission);
  return hasPermission;
};

/**
 * React hook to check if permissions are loaded
 */
export const usePermissionsLoaded = (): boolean => {
  return useStore(state => state.permissionsLoaded || false);
};

/**
 * React hook to get all user permissions
 */
export const useUserPermissions = (): string[] => {
  return useStore(state => state.permissions || []);
};

/**
 * Declarative permission gate component.
 * Renders children only if the current user has the required permission.
 */
export const FeatureGate: React.FC<{
  permission: FeaturePermission;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}> = ({ permission, fallback = null, children }) => {
  const hasPermission = useFeaturePermission(permission);
  return React.createElement(React.Fragment, null, hasPermission ? children : fallback);
};
